import { useCallback, useEffect, useState } from 'react';
import { debounce } from 'lodash';
import { createEditor, Descendant } from 'slate';
import { withReact } from 'slate-react';
import { withHistory } from 'slate-history';
import styled from 'styled-components';
import { useDispatch, useSelector } from 'src/store/store';
import i18n from 'src/utils/translate';
import { useUnsavedChangesPrompt } from 'src/utils/hooks/useUnsavedChangesPrompt';
import { trackFootfallEvent } from 'src/utils/footfall';
import { Event, Section } from 'src/constants/footfall';
import { saveAboutData } from 'src/modules/community/communityAbout/actions';
import { setAlert } from 'src/components/alert/actions';
import RichTextEditor from 'src/components/textEditor/richTextEditor';
import EditorToolbar from 'src/components/textEditor/components/editorToolbar';
import { MyEditor, useEditor } from 'src/components/textEditor/customHelpers';
import { serialise } from 'src/components/textEditor/serialiseContent';
import { composeHtmlStringFromBB, useDeserialiseHtmlString } from 'src/components/textEditor/deserialiseContent';
import { validateAbout } from 'src/modules/community/communityAbout/components/validateAbout';
import Button from 'src/components/buttons/button';
import { MAX_CHARS_COMMUNITY_DESCRIPTION } from 'src/constants/about';

export default function AboutEditor() {
  const dispatch = useDispatch();
  const editor = useEditor(withReact(withHistory(createEditor())));
  const { description, isLoading } = useSelector(({ pages }) => pages.communityAbout);
  const [isSubmitDisabled, setIsSubmitDisabled] = useState(true);
  const [isDirty, setIsDirty] = useState(false);
  const [isEmojiPickerClosed, setIsEmojiPickerClosed] = useState(false);

  const debouncedValidate = useCallback(
    debounce(() => validateAbout({ description: MyEditor.getString(editor, []), setIsDirty, setIsSubmitDisabled }), 200),
    [],
  );
  const editValue = useDeserialiseHtmlString({ text: composeHtmlStringFromBB({ text: description || '' }) });

  const submit = () => {
    // Display errors, if any, after user submitted
    const revalidateErrors = validateAbout({ description: MyEditor.getString(editor, []), setIsDirty, setIsSubmitDisabled });
    if (revalidateErrors.length) {
      return dispatch(setAlert(revalidateErrors[revalidateErrors.length - 1]));
    }

    setIsEmojiPickerClosed(true);
    dispatch(saveAboutData({ description: serialise(editor) }));
    trackFootfallEvent(Event.CLICKED, {
      clickType: 'save-description',
      clickMetadata: { section: Section.CommunityAbout },
    });
  };

  useUnsavedChangesPrompt({ unsavedChanges: isDirty, onAccept: () => MyEditor.reset(editor) });

  useEffect(() => { // Disable Save button when we are saving
    setIsSubmitDisabled(isLoading);
  }, [isLoading]);

  return (
    <EditorContainer data-testid="community-description-editor">
      {editValue && (
        <RichTextEditor
          editor={editor}
          hasMentions={false}
          initialValue={editValue as Descendant[]}
          maxChars={MAX_CHARS_COMMUNITY_DESCRIPTION}
          placeholder={i18n.t('Describe who is this community for and with what objectives...')}
          toolbar={<Toolbar isEmojiPickerClosed={isEmojiPickerClosed} isSubmitDisabled={isSubmitDisabled} submit={submit} />}
          onChange={() => debouncedValidate()}
        />
      )}
    </EditorContainer>
  );
}

const Toolbar = ({ isEmojiPickerClosed, isSubmitDisabled, submit }: { isEmojiPickerClosed: boolean; isSubmitDisabled: boolean, submit: () => void }) => (
  <StyledToolbar
    dataTestId="editor-toolbar"
    hasImage={false}
    hasMentions={false}
    isEmojiPickerClosed={isEmojiPickerClosed}
  >
    <SaveButton
      dataTestId="save-description-button"
      disabled={isSubmitDisabled}
      text={i18n.t('Save')}
      onClick={submit}
    />
  </StyledToolbar>
);

const EditorContainer = styled.div`
  width: 100%;
  height: auto;
  padding: 10px 10px 0;
  border: ${({ theme }) => theme.greyLine};
  &:focus-within {
    border-color: ${({ theme }) => theme.colorBlue};
  }
  > [contenteditable="true"]:focus {
    outline: none;
  }
`;

const StyledToolbar = styled(EditorToolbar)`
  margin-bottom: 0;
  padding: 5px 0;
  line-height: 30px;
`;

const SaveButton = styled(Button)`
  float: right;
  margin: 0;
`;
